import { Fragment, Suspense, useSyncExternalStore, type HTMLAttributes, type ReactNode } from "react";
import { flushSync } from "react-dom";

type View = { identity: string; content: ReactNode };
const views = new Map<string, View>();
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };
const publish = () => { for (const listener of listeners) listener(); };

/** Controller view state. All outlets render as ordinary children of the one Studio root. */
export function setStudioView(name: string, identity: string, content: ReactNode) {
  flushSync(() => { views.set(name, { identity, content }); publish(); });
}
// Panels that slide away instead of vanishing. Only these use a view transition, because its
// update runs a frame later; everything else must stay synchronous.
const SMOOTH_EXITS = new Set(["drawer", "scene-editor-inline"]);
export function clearStudioView(name: string) {
  const leaving = views.get(name);
  const clear = () => flushSync(() => {
    // A panel opened in the meantime is a new view and must never be removed by this exit.
    if (leaving && views.get(name) !== leaving) return;
    views.delete(name);
    publish();
  });
  const doc = typeof document === "undefined" ? undefined : (document as Document & { startViewTransition?: (update: () => void) => unknown });
  const calm = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  if (leaving && SMOOTH_EXITS.has(name) && doc?.startViewTransition && !calm) {
    doc.startViewTransition(clear);
    return;
  }
  clear();
}
export function resetStudioViews() { views.clear(); publish(); }
export function studioViewIdentity(name: string) { return views.get(name)?.identity; }

export function StudioOutlet({ name, children, ...attributes }: HTMLAttributes<HTMLDivElement> & { name: string }) {
  const view = useSyncExternalStore(subscribe, () => views.get(name));
  return <div {...attributes} data-studio-outlet={name}>
    <Suspense fallback={<div className="empty" role="status">Opening this part of the Studio...</div>}>
      {view ? <Fragment key={view.identity}>{view.content}</Fragment> : children}
    </Suspense>
  </div>;
}

export function captureReactFocus(): () => void {
  const focused = document.activeElement;
  if (!(focused instanceof HTMLElement) || !focused.closest("[data-studio-outlet]")) return () => {};
  const text = focused instanceof HTMLTextAreaElement || (focused instanceof HTMLInputElement && ["text", "search", "url", "tel", "password"].includes(focused.type)) ? focused : undefined;
  const start = text?.selectionStart, end = text?.selectionEnd;
  return () => {
    if (!focused.isConnected || (document.activeElement !== document.body && document.activeElement !== focused)) return;
    focused.focus({ preventScroll: true });
    if (text && start != null && end != null) text.setSelectionRange(start, end);
  };
}

export function showStudioMessage(message: string, error = false) {
  setStudioView("app", "workspace-message", <div className={error ? "notice warn" : "empty"} role={error ? "alert" : "status"}>{!error && <span className="loading" />}{message}</div>);
}
export function showWorkflowError(message: string) {
  setStudioView("workflow-error", "workflow-error", <div className="notice warn" role="alert" data-workflow-error><b>Action could not be completed.</b><p>{message}</p></div>);
}
function ProductionWorkspace() { return <><StudioOutlet name="production-shell" id="productionShell" /><StudioOutlet name="workflow-error" /><StudioOutlet name="stage" id="lyricStage" /></>; }
export function showProductionWorkspace(projectId: string) {
  const identity = `production:${projectId}`;
  if (studioViewIdentity("app") !== identity) {
    clearStudioView("production-shell"); clearStudioView("stage"); clearStudioView("workflow-error");
    setStudioView("app", identity, <ProductionWorkspace />);
  }
}
export function showProjectWorkspace(projectId: string, approved: boolean, next: () => void) {
  setStudioView("stage", `project-workspace:${projectId}`, <>
    <div className="lyric-stage-head"><div><div className="eyebrow">Step 1</div><h2>Project & film plan</h2></div>{approved && <button className="btn primary" onClick={next}>Review cast & looks</button>}</div>
    <StudioOutlet name="project-plan" id="projectPlanSlot" /><StudioOutlet name="preproduction" id="preproductionSlot" /><StudioOutlet name="creative-director" id="creativeDirectorSlot" />
  </>);
}
